import { useState } from 'preact/hooks';
import { extractTokens, saveLesson, type IngestPayload } from '../content/ingest';
import { toToneMark } from '../speech/pinyin';
import { SEMANTIC_TAGS, type SemanticTag } from '../content/cardSchema';
import { speakHanzi } from '../speech/webSpeech';

export function IngestScreen({ onClose }: { onClose: () => void }) {
  const [source, setSource] = useState<'paste' | 'ocr'>('paste');
  const [rawText, setRawText] = useState('');
  const [tokens, setTokens] = useState<string[]>([]);
  const [english, setEnglish] = useState<Record<string, string>>({});
  const [title, setTitle] = useState('');
  const [theme, setTheme] = useState<SemanticTag>('general');
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrMsg, setOcrMsg] = useState('');
  const [showAdvancedOcr, setShowAdvancedOcr] = useState(false);

  const extract = (text: string) => {
    setRawText(text);
    setTokens(extractTokens(text));
    setSource('paste');
  };

  const runOcr = async (file: File) => {
    setSource('ocr');
    setOcrBusy(true);
    setOcrMsg('Loading OCR…');
    try {
      // Lazy import — keeps the main bundle small.
      const Tesseract = (await import('tesseract.js')).default;
      setOcrMsg('Reading characters…');
      const result = await Tesseract.recognize(file, 'chi_tra', {
        logger: (m: { status: string; progress: number }) =>
          setOcrMsg(`${m.status} ${(m.progress * 100) | 0}%`),
      });
      const text = (result.data.text || '').trim();
      setRawText(text);
      setTokens(extractTokens(text));
      // Keep source='ocr' (don't fall back through extract() which would reset it).
      setOcrMsg(`done — ${text.length} chars · ${extractTokens(text).length} tokens`);
    } catch (e) {
      setOcrMsg(`OCR failed: ${(e as Error).message}`);
    } finally {
      setOcrBusy(false);
    }
  };

  const remove = (t: string) => {
    setTokens(tokens.filter((x) => x !== t));
    const e2 = { ...english };
    delete e2[t];
    setEnglish(e2);
  };

  const save = async () => {
    if (tokens.length === 0) return;
    const payload: IngestPayload = {
      title: title || `Lesson (${new Date().toLocaleDateString()})`,
      zoneTheme: theme,
      source,
      rawText,
      tokens,
      englishByToken: english,
    };
    await saveLesson(payload);
    onClose();
  };

  return (
    <div class="ws-overlay scrim">
      <div class="ws-panel">
        <div class="ws-row between" style={{ marginBottom: 8 }}>
          <h1 class="ws-h1">Add workbook page</h1>
          <button class="ws-btn ghost" onClick={onClose}>Cancel ✕</button>
        </div>
        <p class="ws-muted">Two ways to get a workbook page into the game. The first is much faster on iPad.</p>

        <div class="ws-card" style={{ background: 'rgba(108, 74, 214, 0.18)', border: '1px solid rgba(108, 74, 214, 0.5)' }}>
          <strong>🍎 Best on iPad — use Apple Live Text</strong>
          <ol style={{ margin: '6px 0 6px 18px', padding: 0, fontSize: 13, lineHeight: 1.6, opacity: 0.9 }}>
            <li>Open the workbook page in the <strong>Photos</strong> app (or Camera).</li>
            <li>Long-press on the Chinese characters → tap <strong>Select All</strong> → <strong>Copy</strong>.</li>
            <li>Come back here and paste below. iPad's on-device OCR is dramatically better than ours for printed Chinese.</li>
          </ol>
        </div>

        <textarea
          class="ws-input ws-textarea"
          placeholder="Paste Chinese characters here (from Live Text, the workbook PDF, anywhere)…  e.g. 貓 狗 魚 鳥"
          value={rawText}
          onInput={(e) => extract((e.target as HTMLTextAreaElement).value)}
          style={{ marginTop: 12 }}
        />

        <div style={{ marginTop: 12 }}>
          <button class="ws-btn ghost" style={{ fontSize: 12, padding: '6px 12px' }} onClick={() => setShowAdvancedOcr(!showAdvancedOcr)}>
            {showAdvancedOcr ? '▾' : '▸'} Advanced: in-app OCR (less accurate)
          </button>
          {showAdvancedOcr && (
            <div class="ws-card" style={{ marginTop: 8 }}>
              <div class="ws-muted" style={{ fontSize: 12, marginBottom: 8 }}>
                Tesseract-based fallback for when Live Text isn't an option. Often produces garbage on real workbook photos — prefer Live Text.
              </div>
              <label class="ws-btn" style={{ display: 'inline-block' }}>
                Take photo
                <input type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={(e) => {
                  const f = (e.target as HTMLInputElement).files?.[0];
                  if (f) runOcr(f);
                }} />
              </label>
              <span class="ws-muted" style={{ marginLeft: 12, fontSize: 12 }}>{ocrBusy ? ocrMsg : (ocrMsg || 'Photo → in-browser OCR')}</span>
            </div>
          )}
        </div>

        {tokens.length > 0 && (
          <>
            <h2 class="ws-h2">Words found ({tokens.length})</h2>
            <p class="ws-muted">Tap a word to remove it. Add the English meaning so the kid sees a hint after a miss.</p>
            <div class="ws-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', marginTop: 8 }}>
              {tokens.map((t) => (
                <div key={t} class="ws-card" style={{ padding: 10 }}>
                  <div class="ws-row between" style={{ alignItems: 'baseline' }}>
                    <div>
                      <div style={{ fontSize: 28 }}>{t}</div>
                      <div class="ws-muted" style={{ fontSize: 12 }}>{toToneMark(t)}</div>
                    </div>
                    <div class="ws-row" style={{ gap: 4 }}>
                      <button class="ws-btn ghost" style={{ padding: '6px 10px' }} onClick={() => speakHanzi(t)}>🔊</button>
                      <button class="ws-btn ghost" style={{ padding: '6px 10px' }} onClick={() => remove(t)}>✕</button>
                    </div>
                  </div>
                  <input
                    class="ws-input"
                    placeholder="English"
                    style={{ marginTop: 6, fontSize: 13 }}
                    value={english[t] ?? ''}
                    onInput={(e) => setEnglish({ ...english, [t]: (e.target as HTMLInputElement).value })}
                  />
                </div>
              ))}
            </div>

            <h2 class="ws-h2">Lesson details</h2>
            <div class="ws-grid" style={{ gridTemplateColumns: '2fr 1fr', gap: 10 }}>
              <input class="ws-input" placeholder="Lesson title (e.g. Week 3 Animals)" value={title} onInput={(e) => setTitle((e.target as HTMLInputElement).value)} />
              <select class="ws-input" value={theme} onChange={(e) => setTheme((e.target as HTMLSelectElement).value as SemanticTag)}>
                {SEMANTIC_TAGS.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div class="ws-row between" style={{ marginTop: 14 }}>
              <span class="ws-muted">Theme decides which monsters you'll fight.</span>
              <button class="ws-btn big" onClick={save}>Create zone →</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
